import {
  AppBar,
  Button,
  Container,
  createMuiTheme,
  Dialog,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {green} from '@material-ui/core/colors';
import CloseIcon from '@material-ui/icons/Close';
import React, {useReducer} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ContingentBeneficiaryData from './ContingentBeneficiaryData';

import * as I from 'immutable';
import * as CBR from './ContingentBeneficiaryReducer';

import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import {addContingentBeneficiary, updateContingentBeneficiary} from 'ApplicationEntry/ducks/contingentBeneficiaryList';
import {
  ContingentBeneficiary,
  ContingentBeneficiaryActionType,
  EmptyContingentBeneficiary,
  isInvalidContingentBeneficiary,
} from 'ApplicationEntry/models/ContingentBeneficiary';
import {MAX_CONTINGENT_BENEFICIARY} from 'ApplicationEntry/models/ContingentBeneficiary';
import {PhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import Loading from 'Shared/components/Loading';

import './ContingentBeneficiary.sass';
import {matchApplication, InvalidApplication, ValidApplication} from 'ApplicationEntry/models/Application';
import {InvalidModalApplication, matchModalApplication, ValidModalApplication} from 'ApplicationEntry/models/ModalApplication';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';

const theme = createMuiTheme({
  palette: {
    primary: green,
  },
});

type DialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  dialogTitle: string;
  dialogSubTitle: string;
  dialogAdditional: string;
  contingentBeneficiaryIndex: number;
  editContingentBeneficiary: boolean;
};

const ContingentBeneficiaryDialog = (props: DialogProps) => {
  const applicationState = useSelector((state: ReducerState) => state.application);

  const initialState: CBR.State = {members: I.List<ContingentBeneficiary>().push(EmptyContingentBeneficiary)};
  const [localState, dispatch] = useReducer(CBR.reducer, initialState);
  const dispatchRedux = useDispatch();

  const application = (applicationState as InvalidModalApplication | ValidModalApplication).application as
    | InvalidApplication
    | ValidApplication;

  const contingentBeneficiaryList = matchApplication(application, {
    Valid: valid => valid.contingentBeneficiaryList,
    Invalid: invalid => invalid.contingentBeneficiaryList,
    Undetermined: () => undefined,
  });

  if (applicationState.key === 'LoadingModalApplication') {
    return <Loading />;
  } else if (applicationState.key === 'UndeterminedModalApplication') {
    return <Redirect to={ApplicationEntryRouteConstants.WorkQueue} />;
  }

  if (props.editContingentBeneficiary && localState.members.toArray()[0].key === 'UnvalidatedContingentBeneficiary') {
    dispatch(
      CBR.editContingentBeneficiary(
        contingentBeneficiaryList!.contingentBeneficiaryList.toArray()[props.contingentBeneficiaryIndex]
      )
    );
  }

  const handleSave = () => {
    props.editContingentBeneficiary
      ? dispatchRedux(updateContingentBeneficiary(localState.members.toArray()[0], props.contingentBeneficiaryIndex))
      : dispatchRedux(addContingentBeneficiary(localState.members));
    dispatch(CBR.emptyContingentBeneficiary());

    props.dialogClose();
  };

  const checkTotalPercent = () => {
    const totalPercentage =
      contingentBeneficiaryList!.contingentBeneficiaryList.reduce((a, b) => a + b.percent, 0) +
      localState.members.reduce((a, b) => a + b.percent, 0);
    return props.editContingentBeneficiary
      ? totalPercentage -
          contingentBeneficiaryList!.contingentBeneficiaryList.toArray()[props.contingentBeneficiaryIndex].percent
      : totalPercentage;
  };

  const handleCancel = () => {
    dispatch(CBR.emptyContingentBeneficiary());
    props.dialogClose();
  };

  return (
    <Dialog fullScreen open={props.dialogOpen} onClose={props.dialogClose}>
      <AppBar className="contingent-beneficiary-section__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={props.dialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className="contingent-beneficiary-section__dialog-title">
            {props.dialogTitle}
          </Typography>
        </Toolbar>
      </AppBar>

      <div className="contingent-beneficiary-section__dialog">
        <Container className="contingent-beneficiary-section__dialog-container" maxWidth={false}>
          <div className="contingent-beneficiary-section__dialog-container__title-container">
            <Typography
              variant="subtitle1"
              className="contingent-beneficiary-section__dialog-container__title-container-title"
              color="primary"
            >
              {props.dialogSubTitle}
            </Typography>
          </div>

          {localState.members.map((ele, i) => (
            <ContingentBeneficiaryData
              key={i}
              index={i}
              contingentBeneficiaryIndex={props.contingentBeneficiaryIndex}
              editContingentBeneficiary={props.editContingentBeneficiary}
              removeContingentBeneficiary={() => dispatch(CBR.removeContingentBeneficiary(i))}
              changeFirstName={(value: string) => dispatch(CBR.updateFirstName(i, value))}
              changeMiddleName={(value: string) => dispatch(CBR.updateMiddleName(i, value))}
              changeLastName={(value: string) => dispatch(CBR.updateLastName(i, value))}
              changeRelationship={(value: string) => dispatch(CBR.updateRelationship(i, value))}
              changeDOB={(value: Date | '') => dispatch(CBR.updateDOB(i, value))}
              changeSSN={(value: string) => dispatch(CBR.updateSSN(i, value))}
              changePercent={(value: number) => dispatch(CBR.updatePercent(i, value))}
              changeAddress={(value: string) => dispatch(CBR.updateAddress(i, value))}
              changeAddressLine2={(value: string) => dispatch(CBR.updateAddressLine2(i, value))}
              changeCity={(value: string) => dispatch(CBR.updateCity(i, value))}
              changeState={(value: string) => dispatch(CBR.updateState(i, value))}
              changeZip={(value: ZipCode) => dispatch(CBR.updateZip(i, value))}
              changePhone={(value: PhoneNumber) => dispatch(CBR.updatePhone(i, value))}
              changeSignatureDate={(value: Date | '') => dispatch(CBR.updateSignatureDate(i, value))}
              members={ele}
              contingentBeneficiaryListLength={contingentBeneficiaryList!.contingentBeneficiaryList.size}
              totalPercent={checkTotalPercent()}
              modalApplication={applicationState}
            />
          ))}
          {!props.editContingentBeneficiary &&
          contingentBeneficiaryList!.contingentBeneficiaryList.size + localState.members.size <
            MAX_CONTINGENT_BENEFICIARY &&
          checkTotalPercent() < 100 ? (
            <div className="contingent-beneficiary-section__dialog-container-addbutton">
              <Button
                variant="contained"
                color="primary"
                onClick={() => dispatch({type: ContingentBeneficiaryActionType.ADD_CONTINGENTBENEFICIARY})}
              >
                <Typography variant="h6">+ </Typography>
                {props.dialogAdditional}
              </Button>
            </div>
          ) : null}

          <div className="contingent-beneficiary-section__buttons-container">
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCancel}
              className="contingent-beneficiary-section__buttons-container-buttons"
            >
              Cancel
            </Button>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="primary"
                className="contingent-beneficiary-section__buttons-container-__buttons"
                onClick={handleSave}
                disabled={localState.members.some(isInvalidContingentBeneficiary) || checkTotalPercent() > 100}
              >
                Save Changes
              </Button>
            </ThemeProvider>
          </div>
        </Container>
      </div>
    </Dialog>
  );
};

export default ContingentBeneficiaryDialog;
